import { Fragment } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import type { PartnerActivityEntry } from "../../model";
import { ACTIVITY_COLUMNS, activityColumn } from "../activityColumns";
import { ActivityCellContent } from "./ActivityCellContent";

interface PartnerActivityTileProps {
  entry: PartnerActivityEntry;
}

/**
 * How to read one ACTIVITY_COLUMNS field's value off an entry — "time" and
 * "status" aren't here, they're the card header instead of a body row.
 * Reading labels + the column set from ACTIVITY_COLUMNS (rather than a
 * second hardcoded list) means a column added/renamed/reordered there can't
 * silently drift out of sync with what the tile shows — same fix CPD-70's
 * own PartnerMetricsTile got in review.
 */
const BODY_COLUMNS = ACTIVITY_COLUMNS.filter((column) => column.tileSlot === "body");

/**
 * One activity entry as a card — the mobile alternative to a
 * `PartnerActivityTable` row. Same fields as `ACTIVITY_COLUMNS`, same
 * Hit/Error chip treatment as the table.
 */
export function PartnerActivityTile({ entry }: PartnerActivityTileProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <ActivityCellContent cell={activityColumn("time").project(entry)} />
          </Typography>
          <ActivityCellContent cell={activityColumn("status").project(entry)} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.5, columnGap: 1.5 }}>
          {BODY_COLUMNS.map((column) => (
            <Fragment key={column.id}>
              <Typography variant="body2" color="text.secondary">
                {column.label}
              </Typography>
              <Typography variant="body2" textAlign="right">
                <ActivityCellContent cell={column.project(entry)} />
              </Typography>
            </Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
